<script setup lang="ts">
import AccountMenu from '@/components/layout/AccountMenu.vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import useAuthorization from '@/composables/useAuthorization'
import { PermissionOrganization } from '@/generated/graphql'
import { currentOrganization } from '@/app'
import { computed } from 'vue'

defineEmits<{
  (e: 'toggleDrawer'): void
}>()

const { t } = useI18n()
const router = useRouter()
const { reactiveOrgAuthz, canAccessOperatorHome } = useAuthorization()

const route = useRoute()
const routeName = computed(() => (route.params.organizationSlug ? 'orgHome' : 'home'))
</script>

<template>
  <v-app-bar class="main-app-bar">
    <template #title>
      <router-link class="mainTitle" :to="{ name: routeName }">
        <v-img width="150" src="/img/logo.svg" title="Baseboard" />
      </router-link>
    </template>
    <template #prepend>
      <v-app-bar-nav-icon @click="$emit('toggleDrawer')" />
    </template>
    <template #append>
      <template v-if="currentOrganization">
        <v-btn
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgMember).value"
          icon="apartment"
          :title="t('component.mainAppBar.buttonOrgHome')"
          @click="router.push({ name: 'orgHome' })"
        />
        <v-btn
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductRead).value"
          icon="precision_manufacturing"
          :title="t('entity.product.plural')"
          @click="router.push({ name: 'products' })"
        />
        <v-btn
          v-if="canAccessOperatorHome()"
          icon="warehouse"
          :title="t('component.mainAppBar.buttonOperator')"
          @click="router.push({ name: 'operatorHome' })"
        />
        <v-btn
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgProductionBatchRead).value"
          icon="library_add_check"
          :title="t('entity.productionBatch.plural')"
          @click="router.push({ name: 'production-batches' })"
        />
        <v-btn
          v-if="reactiveOrgAuthz(PermissionOrganization.OrgIssueRead).value"
          icon="flag"
          class="mr-5"
          :title="t('entity.issue.plural')"
          @click="router.push({ name: 'issues' })"
        />
      </template>
      <account-menu />
    </template>
  </v-app-bar>
</template>

<style lang="scss">
@use 'sass:color';

@media print {
  .v-app-bar {
    display: none !important;
  }
}

.v-app-bar.v-toolbar.main-app-bar {
  background: rgb(var(--v-theme-primary));
  color: color.scale(#fff, $lightness: -10%);

  .v-app-bar-nav-icon,
  .mainTitle,
  a {
    color: color.scale(#fff, $lightness: -10%);
  }

  a {
    margin-left: 0.5em;
  }

  a:first-of-type {
    margin-left: 0;
  }

  a:hover {
    text-decoration: none;
  }
}
</style>
